import { useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useAuth } from '../auth/useAuth'
import { getErrorMessage } from '../../lib/error-message'
import {
  buildVisualEstimateDescription,
  getVisualEstimateRequestDraft,
  publishRequestFromVisualEstimate,
} from './visual-estimate-request-service'
import './amigo-visual-estimate.css'
import { useI18n } from '../i18n/useI18n'

function formString(form: FormData, field: string) {
  const value = form.get(field)
  return typeof value === 'string' ? value : ''
}

export function VisualEstimateRequestPage() {
  const { estimateId } = useParams({ from: '/authenticated/marketplace/visual-estimate/$estimateId/request' })
  const { user } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const [notice, setNotice] = useState('')
  const draft = useQuery({ queryKey: ['visual-estimate-request-draft', estimateId], queryFn: () => getVisualEstimateRequestDraft(estimateId) })
  const publishMutation = useMutation({
    mutationFn: publishRequestFromVisualEstimate,
    onSuccess: async () => {
      setNotice('Marketplace request published with the AMIGO estimate and project photos attached.')
      await navigate({ to: '/marketplace' })
    },
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!draft.data || !user) return
    const form = new FormData(event.currentTarget)
    publishMutation.mutate({
      draft: draft.data,
      userId: user.id,
      title: formString(form, 'title'),
      description: formString(form, 'description'),
      city: formString(form, 'city'),
      region: formString(form, 'region'),
      postalCode: formString(form, 'postal'),
      desiredOn: formString(form, 'date') || null,
    })
  }

  const error = draft.error ?? publishMutation.error
  return <section className="marketplace-page amigo-visual-estimate" aria-labelledby="estimate-request-title">
    <p className="eyebrow">AMIGO Visual Estimate™</p>
    <div className="page-heading">
      <div><h1 id="estimate-request-title">Publish the estimated project</h1><p className="lede">Review the prefilled request. The original estimate and photos remain attached automatically.</p></div>
      <Link to="/marketplace/visual-estimate" className="text-button">Back to estimate</Link>
    </div>
    {notice && <div className="form-success" role="status">{notice}</div>}
    {error && <div className="form-error" role="alert">{getErrorMessage(error, 'Unable to prepare the marketplace request.')}</div>}
    {draft.isLoading && <div className="page-state">Preparing your request…</div>}
    {draft.data && <div className="marketplace-grid">
      <article className="panel">
        <h2>Marketplace request</h2>
        {draft.data.service_request_id ? <div className="form-success">This estimate has already been published. <Link to="/marketplace">Open marketplace</Link></div> : <form className="marketplace-form" onSubmit={submit}>
          <label>Property<input value={draft.data.properties?.name ?? 'Selected property'} readOnly /></label>
          <label>Category<input value={draft.data.service_categories?.name ?? 'Property service'} readOnly /></label>
          <label className="span-2">Title<input name="title" required defaultValue={`${draft.data.service_categories?.name ?? 'Property'} project`} /></label>
          <label className="span-2">Description<textarea name="description" required defaultValue={buildVisualEstimateDescription(draft.data)} /></label>
          <label>City<input name="city" required defaultValue={draft.data.properties?.city ?? ''} /></label>
          <label>State / region<input name="region" required defaultValue={draft.data.properties?.region ?? ''} /></label>
          <label>Postal code<input name="postal" required defaultValue={draft.data.properties?.postal_code || draft.data.postal_code} /></label>
          <label>Desired date<input name="date" type="date" /></label>
          <div className="span-2 form-note">{t('Professionals will submit independent offers based on the confirmed scope.')}</div>
          <button className="span-2" disabled={publishMutation.isPending}>{publishMutation.isPending ? 'Publishing request…' : 'Publish request with estimate and photos'}</button>
        </form>}
      </article>
      <article className="panel">
        <h2>Attached automatically</h2>
        <div className="visual-estimate-result">
          <div className="marketplace-status-row"><strong>{t('AMIGO estimate attached privately')}</strong><span className="status-pill">{t('Internal')}</span></div>
          <p>{t('The estimate remains attached as internal pricing intelligence and is not shown as a customer budget.')}</p>
          <p>{draft.data.marketplace_visual_estimate_images.length} project photo(s) linked privately.</p>
          <h3>Visible scope</h3><ul>{draft.data.scope_items.map((item) => <li key={item}>{item}</li>)}</ul>
          <h3>Possible price changes</h3><ul>{draft.data.price_factors.map((item) => <li key={item}>{item}</li>)}</ul>
          <p className="form-note">{draft.data.disclaimer}</p>
        </div>
      </article>
    </div>}
  </section>
}
